import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Package, 
  Search, 
  Image as ImageIcon,
  AlertCircle,
  Tag,
  Grid,
  Settings
} from 'lucide-react';
import AddCategoryModal from '../AddCategoryModal';
import UpdateCategoryModal from '../UpdateCategoryModal';
import CategoryProductsModal from '../CategoryProductsModal';
import { API_BASE_URL } from '../../config';

const Categories = ({ authToken }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [imageRefreshKey, setImageRefreshKey] = useState(0);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  // Fetch categories from API with real-time product counts
  const fetchCategories = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/getcategories`, {
        headers: getAuthHeaders(),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Get real-time product counts for each category using the same logic as CategoryProductsModal
        const categoriesWithRealCounts = await Promise.all(
          data.categories.map(async (category) => {
            try {
              const productsResponse = await fetch(`${API_BASE_URL}/category/${category.category_id}/products`, {
                headers: getAuthHeaders(),
              });
              
              if (productsResponse.ok) {
                const productsData = await productsResponse.json();
                return {
                  ...category,
                  products_count: productsData.products ? productsData.products.length : 0
                };
              }
            } catch (err) {
              console.error(`Error fetching products for category ${category.category_id}:`, err);
            }
            
            return category;
          })
        );
        
        setCategories(categoriesWithRealCounts);
      } else {
        setError(data.error || 'Failed to fetch categories');
      }
    } catch (err) {
      setError('Network error. Please check if server is running.');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete category
  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/deletecategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ category_id: categoryId }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCategories(categories.filter(c => c.category_id !== categoryId));
      } else {
        alert(data.error || 'Failed to delete category');
      }
    } catch (err) {
      alert('Error deleting category');
      console.error('Delete error:', err);
    }
  };

  // Get category image URL
  const getCategoryImageUrl = (category) => {
    if (category.image_path) {
      return `${API_BASE_URL}${category.image_path}?refresh=${imageRefreshKey}`;
    }
    return null;
  };

  // Handle category added/updated
  const handleCategoryAdded = () => {
    setImageRefreshKey(prev => prev + 1);
    fetchCategories();
  };

  const handleCategoryUpdated = () => {
    setImageRefreshKey(prev => prev + 1);
    fetchCategories();
    setShowEditModal(false);
  };

  // Handle products updated in category
  const handleProductsUpdated = () => {
    fetchCategories(); // Refresh to update product counts
  };

  // Filter categories based on search
  const filteredCategories = categories.filter(category =>
    category.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600">
            {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Category</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
          // Loading skeletons
          Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
              <div className="w-full h-32 bg-gray-200 rounded-lg mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3 mb-4"></div>
              <div className="flex justify-between items-center">
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="flex space-x-2">
                  <div className="h-8 bg-gray-200 rounded w-8"></div>
                  <div className="h-8 bg-gray-200 rounded w-8"></div>
                </div>
              </div>
            </div>
          ))
        ) : filteredCategories.length > 0 ? (
          filteredCategories.map((category) => (
            <div key={category.category_id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              {/* Category Image */}
              <div className="relative">
                {getCategoryImageUrl(category) ? (
                  <img
                    src={getCategoryImageUrl(category)}
                    alt={category.category_name}
                    className="w-full h-32 object-cover rounded-t-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-100 rounded-t-lg flex items-center justify-center">
                    <Tag className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                
                {/* Fallback when image doesn't load */}
                <div className="w-full h-32 bg-gray-100 rounded-t-lg hidden items-center justify-center">
                  <Tag className="h-8 w-8 text-gray-400" />
                </div>
              </div>

              {/* Category Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2 truncate">
                  {category.category_name}
                </h3>
                
                <div className="flex items-center text-sm text-gray-600 mb-4">
                  <Package className="h-4 w-4 mr-1" />
                  <span>{category.products_count} products</span>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-3 gap-1">
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowProductsModal(true);
                    }}
                    className="bg-green-50 text-green-600 py-2 px-2 rounded-md hover:bg-green-100 transition-colors text-xs font-medium flex items-center justify-center"
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    Manage
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowEditModal(true);
                    }}
                    className="bg-blue-50 text-blue-600 py-2 px-2 rounded-md hover:bg-blue-100 transition-colors text-xs font-medium flex items-center justify-center"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => deleteCategory(category.category_id)}
                    className="bg-red-50 text-red-600 py-2 px-2 rounded-md hover:bg-red-100 transition-colors text-xs font-medium flex items-center justify-center"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          // Empty state
          <div className="col-span-full">
            <div className="text-center py-12">
              <Grid className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : 'Get started by creating your first category'
                }
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Your First Category</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      <AddCategoryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCategoryAdded={handleCategoryAdded}
      />

      {/* Update Category Modal */}
      <UpdateCategoryModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onCategoryUpdated={handleCategoryUpdated}
        category={selectedCategory}
      />

      {/* Category Products Modal */}
      <CategoryProductsModal
        isOpen={showProductsModal}
        onClose={() => setShowProductsModal(false)}
        onProductsUpdated={handleProductsUpdated}
        category={selectedCategory}
      />
    </div>
  );
};

export default Categories;